import { Link } from "gatsby"
import * as React from "react"
import { Heading } from "../components/Agenda"
import Article from "../components/Article"
import Layout from "../components/Layout"
import { MainHeaders } from "../components/MainHeader/types/mainHeaders"
import Paragraph from "../components/Paragraph"
import Texts, { TextColor } from "../components/Texts"
import Video from "../components/Video"
import {
  bottomFrame,
  downloadKit,
  downloadKit2,
  middleMiddleFrame,
  topFrame,
  topWhole,
} from "../static/images/hardware"
import Image from "../components/Image"
import Button from "../components/Button"
import saveAs from "file-saver"
import ReferenceList from "../components/ReferenceList"

const HardwarePage = () => {
  const hardwareRef = "hardware-ref"
  const spacingClassName = "p-4 md:p-8"
  const spacingSmaller = "p-2 md:p-4"
  return (
    <Layout
      header={MainHeaders.Hardware}
      isWithAgenda
      isWithBackToTop
      agendaTitle="Hardware"
      agendaHeight="h-[50vh]"
    >
      <Article>
        <Texts.Header1>
          <Heading as="h1" id="3d-custom-model">
            3D custom microplate model
          </Heading>
        </Texts.Header1>
        <div className={spacingSmaller} />
        <Paragraph>
          While developing our nanoplastic detection system, we felt the need
          (aspired) for a rather <Texts.Body2>unconventional</Texts.Body2> microplate setting. Even though
          a manually altered microplate was used throughout the iGEM
          competition, we noticed that there was an absence of a properly
          designed and ready-to-use solution. Hence, we propose a 3D model for a
          custom glass microplate with incorporated cellulose membrane. The
          hardware is a <Texts.Body2>sustainable</Texts.Body2> alternative to
          plastic microplates.
        </Paragraph>
        <div className={spacingSmaller} />
        <div className="flex w-full justify-center">
          <Video
            title={"Vilnius-Lithuania: Plate Model (2022) [English]"}
            width={560}
            height={315}
            url={
              "https://video.igem.org/videos/embed/47277577-81ec-438c-988a-65cb22884861"
            }
          />
        </div>
        <div className={spacingClassName} />
        <Texts.Header1>
          <Heading as="h1" id="challenges-nano-find">
            Challenges for the NanoFind detection system
          </Heading>
        </Texts.Header1>
        <div className={spacingSmaller} />
        <Paragraph>
          As the <Texts.Body2>primary goal</Texts.Body2> of the NanoFind system is to detect nanoplastic
          particles in water samples, plates made from plastic were not an
          option. Due to the engineered peptides' high affinity for plastic
          surfaces, false-positive results may be obtained. In addition, the
          plastic-binding peptides conjugated with a cellulose-binding domain
          required a cellulose base for envisioned immobilization of the whole
          detection system. Hence, two key features, <Texts.Body2>glass surface</Texts.Body2> and
          {" "}<Texts.Body2>incorporation of cellulose</Texts.Body2>, were crucial for a feasible testing of our
          engineered peptide complex.
        </Paragraph>
        <div className={spacingSmaller} />
        <Paragraph>
          While performing experiments over the duration of the project, we
          managed to find a temporary DIY solution - conventional glass-coated
          plastic plates with addition of manually cut cellulose circles.
          However, having in mind a proposed project implementation - the{" "}
          <Link to="/implementation" className="underline">
            nanoplastic detection kit
          </Link>{" "}
          - we created this 3D microplate model.
        </Paragraph>
        <div className={spacingClassName} />
        <Texts.Header1>
          <Heading as="h1" id="universal-problems">
            Universal problems
          </Heading>
        </Texts.Header1>
        <div className={spacingSmaller} />
        <Paragraph>
          Most of the biological assays are performed using laboratory
          plasticware such as pipette tips, microplates, tubes, and etc. Despite
          the universal application, <Texts.Body2>plastic degradation</Texts.Body2> and <Texts.Body2>leaching of
          contaminants</Texts.Body2> or impurities from plasticware can have a significant
          impact on the performed bioassays{" "}
          <Texts.Reference id={hardwareRef}>[1]</Texts.Reference>. As a result,
          the inertness of glass material could be more favorable over plastic.
          Yet glassware is still compatible with optical systems and may even
          outweigh the quality of results compared to the use of plasticware{" "}
          <Texts.Reference id={hardwareRef}>[2]</Texts.Reference>.
        </Paragraph>
        <div className={spacingClassName} />
        <Texts.Header1>
          <Heading as="h1" id="3d-model">
            3D model
          </Heading>
        </Texts.Header1>
        <div className={spacingSmaller} />
        <Paragraph>
          The proposed hardware contains <Texts.Body2>three parts</Texts.Body2> - a bottom frame of a
          plate, cellulose sheet, and a top plate with clamps on the sides. The
          form and size of the microplate matches a standard 96-well plate, yet
          each detachable part was specifically tailored to aid its function.
        </Paragraph>
        <div className={spacingSmaller} />
        <Texts.Subtitle>
          <Heading as="h2" id="glass-bottom-frame">
            Glass bottom frame
          </Heading>
        </Texts.Subtitle>
        <div className={spacingSmaller} />
        <Paragraph>
          Regarding the bottom part of the plate, it is designed as a
          rectangular glass frame with 96 raised and {" "}<Texts.Body2>rounded knobs</Texts.Body2>. The aim was
          to create such a hardware system that would enable tight installment
          of the top plate wells onto the knobs on the bottom. Hence, the size
          of the knobs {" "}<Texts.Body2>complement</Texts.Body2> the size of the inner parts of the projected
          wells.
        </Paragraph>
        <div className={spacingSmaller} />
        <Image label="bottom-frame" url={bottomFrame} />
        <div className={spacingSmaller} />
        <Texts.Subtitle>
          <Heading as="h2" id="middle-cell-frame">
            Middle cellulose sheet
          </Heading>
        </Texts.Subtitle>
        <div className={spacingSmaller} />
        <Paragraph>
          For the middle part of our custom glass microplate, we are using a
          sheet of cellulose, which is dedicated to the {" "}<Texts.Body2>immobilization</Texts.Body2> of the
          engineered peptides. The <Texts.Body2>great advantage</Texts.Body2> of this part is that instead
          of using disposable plates, it allows changing only the sheet itself.
        </Paragraph>
        <div className={spacingSmaller} />
        <Image label="middle-middle-sheet" url={middleMiddleFrame} />
        <div className={spacingSmaller} />
        <Texts.Subtitle>
          <Heading as="h2" id="glass-top-frame">
            Glass top frame
          </Heading>
        </Texts.Subtitle>
        <div className={spacingSmaller} />
        <Paragraph>
          Finally, the last piece to our hardware is the top glass plate  
          incorporating projections of the wells. In addition, <Texts.Body2>two clamps</Texts.Body2> on the
          sides are tailored to support the tighter assemblance of the
          framework. As a result, the cellulose membrane is fully squeezed in
          between two different glass sheets, therefore the bottom parts of the
          wells are <Texts.Body2>equally covered</Texts.Body2> in cellulose. This resolves the problem of
          inconsistent sizing of the manually cut cellulose circles.
        </Paragraph>
        <div className={spacingSmaller} />
        <Image label="top-frame" url={topFrame} />
        <div className={spacingSmaller} />
        <Paragraph>
          Our 3D model is a custom <Texts.Body2>glass</Texts.Body2> microplate incorporating a cellulose
          membrane. The versatility of our proposed microplate originates from
          the <Texts.Body2>detachable design</Texts.Body2>. Not only could the microplate be used with a
          cellulose sheet but it could also be exploited using <Texts.Body2>sheets of other
          materials</Texts.Body2> or as without any incorporated sheets a simple glass
          plate. The model is <Texts.Body2>universal</Texts.Body2> for liquid-based samples and 
          {" "}<Texts.Body2>compatible</Texts.Body2> with the fluorescence measurement methods due to the black frame,
          which reduces background noise.
        </Paragraph>
        <div className={spacingSmaller} />
        <Image label="whole-frame" url={topWhole} />
        <div className={spacingSmaller} />
        <Button
          onClick={() => {
            saveAs(downloadKit, "microplate-top.stl")
            saveAs(downloadKit2, "microplate_bottom.stl")
          }}
        >
          <Texts.Body1 color={TextColor.White}>
            Download the microplate 3D model
          </Texts.Body1>
        </Button>
        <div className={spacingClassName} />
        <Texts.Figure>References:</Texts.Figure>
        <ReferenceList id={hardwareRef}>
          [1] McDonald, G. R., Hudson, A. L., Dunn, S. M., You, H., Baker, G.
          B., Whittal, R. M., Martin, J. W., Jha, A., Edmondson, D. E., & Holt,
          A. (2008). Bioactive contaminants leach from disposable laboratory
          plasticware. Science (New York, N.Y.), 322(5903), 917.{" "}
          <Texts.RefLink to="https://doi.org/10.1126/science.1162395">
            https://doi.org/10.1126/science.1162395
          </Texts.RefLink>{" "}
          <br />
          [2] Stadtfeld, M., Varas, F., & Graf, T. (2005). Fluorescent
          protein-cell labeling and its application in time-lapse analysis of
          hematopoietic differentiation. Methods in molecular medicine, 105,
          395-412.{" "}
          <Texts.RefLink to="https://doi.org/10.1385/1-59259-826-9:395">
            https://doi.org/10.1385/1-59259-826-9:395
          </Texts.RefLink>
        </ReferenceList>
      </Article>
    </Layout>
  )
}

export default HardwarePage
